import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class CryptoService {

  private readonly algorithm = 'AES-CBC';
  private readonly keyString = 'base64:2C6C1I0R9K2';

  /**
   * Same as:
   * substr(hash('sha256', key), 0, 32)
   */
  private async deriveKey(): Promise<CryptoKey> {

    const hashBuffer = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(this.keyString)
    );

    // Convert hash to hex string
    const hashHex = Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');

    // First 32 hex characters (same as Laravel)
    const keyBytes = new TextEncoder().encode(hashHex.substring(0, 32));

    return crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: this.algorithm },
      false,
      ['encrypt', 'decrypt']
    );
  }

  async encrypt(plainText: string): Promise<string> {

    const key = await this.deriveKey();

    const iv = crypto.getRandomValues(new Uint8Array(16));

    const encrypted = await crypto.subtle.encrypt(
      {
        name: this.algorithm,
        iv
      },
      key,
      new TextEncoder().encode(plainText)
    );

    // Combine IV + ciphertext
    const result = new Uint8Array(iv.length + encrypted.byteLength);

    result.set(iv, 0);
    result.set(new Uint8Array(encrypted), iv.length);

    return btoa(String.fromCharCode(...result));
  }

  async decrypt(base64: string): Promise<string> {

    const key = await this.deriveKey();

    const data = Uint8Array.from(
      atob(base64),
      c => c.charCodeAt(0)
    );

    const iv = data.slice(0, 16);
    const ciphertext = data.slice(16);

    const decrypted = await crypto.subtle.decrypt(
      {
        name: this.algorithm,
        iv
      },
      key,
      ciphertext
    );

    return new TextDecoder().decode(decrypted);
  }
}